import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  ChevronRight,
  LocateFixed,
  LogOut,
  Menu,
  MessageCircleMore,
  Search,
  Settings,
  Trash2,
  Trophy,
  UserRound,
} from "lucide-react";
import LanguageSwitcher from "./LanguageSwitcher";
import FriendNotifications from "./FriendNotifications";
import SettingsPanel from "./SettingsPanel";
import API_BASE, { withApiCredentials } from "../api";
import { getLegacyAppToken } from "../auth/bootstrapTokens";
import {
  reportBackgroundRefreshBlocked,
  shouldBlockBackgroundRefresh,
} from "../app/catchSubmitGate";
import { searchMapLocations } from "../utils/mapLocationSearch";
import { APP_MODES } from "../utils/appMode";
import { shouldHideMobileMapSearch } from "./navBannerVisibility";

const REFRESH_APP_CHROME_METRICS_EVENT = "reelitpro:refresh-app-chrome-metrics";
const DEFAULT_AVATAR = "https://cdn-icons-png.flaticon.com/512/847/847969.png";

function normalizeAvatarPosition(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 50;
  return Math.min(100, Math.max(0, parsed));
}

function resolveAvatarObjectPosition(user) {
  const positionX = normalizeAvatarPosition(user?.avatarPositionX);
  const positionY = normalizeAvatarPosition(user?.avatarPositionY);
  return `${positionX}% ${positionY}%`;
}

function resolveAvatarScale(user) {
  const parsed = Number(user?.avatarScale);
  if (!Number.isFinite(parsed) || parsed <= 0) return 1;
  return parsed;
}

function appendAvatarCacheParam(src, updatedAt) {
  if (!src || !updatedAt) return src;
  return `${src}${src.includes("?") ? "&" : "?"}t=${encodeURIComponent(updatedAt)}`;
}

function resolveAvatarSrc(user) {
  const avatar = String(user?.avatar || "").trim();
  if (!avatar) return DEFAULT_AVATAR;

  const src = /^https?:\/\//i.test(avatar) ? avatar : `${API_BASE}${avatar}`;
  return appendAvatarCacheParam(src, user?.updatedAt || user?.updated_at);
}

export default function NavBanner({
  currentUser,
  useReelitProChrome = false,
  activeTab,
  setShowMenu,
  showMenu,
  setShowProfile,
  authTransitioning = false,
  onRequestDeleteAccount,
  onRequestLogout,
  onLogout,
  searchQuery,
  setSearchQuery,
  searchResults,
  onSelectSearchUser,
  groupSearchResults,
  onSelectSearchGroup,
  onOpenNotificationPost,
  setShowInbox,
  onOpenMessagesInbox,
  setSidebarOpen,
  onMapLocateMe,
  isCatchMarkerDetailsOpen = false,
  appMode = APP_MODES.FULL,
  onAppModeChange,
  themeMode,
  onThemeModeChange,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [unreadCount, setUnreadCount] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mapSearchResults, setMapSearchResults] = useState([]);
  const [mapSearchLoading, setMapSearchLoading] = useState(false);
  const [mapSearchError, setMapSearchError] = useState("");
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );

  const token = getLegacyAppToken(window);
  const hasAuthenticatedUser = Boolean(
    token || currentUser?.id || currentUser?.username
  );
  const profileMenuRef = useRef(null);
  const profileIconRef = useRef(null);
  const searchWrapRef = useRef(null);
  const navbarRef = useRef(null);
  const mapSearchAbortRef = useRef(null);
  const previousUnreadCountRef = useRef(null);
  const unreadRequestInFlightRef = useRef(false);
  const queuedUnreadRefreshRef = useRef(false);
  const activeUnreadPromiseRef = useRef(null);

  const playIncomingMessageSound = () => {
    const audio = new Audio(
      process.env.PUBLIC_URL + "/sound-efects/fish-splash.mp3"
    );
    audio.volume = 0.8;
    audio.play().catch(() => {});
  };

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleResize = () => {
      setIsMobileViewport(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  useEffect(() => {
    const rootStyle = document.documentElement.style;
    let frameId = null;
    let settleTimerId = null;

    const updateNavbarHeight = () => {
      const nextHeight = navbarRef.current?.getBoundingClientRect?.().height;
      if (!Number.isFinite(nextHeight) || nextHeight <= 0) {
        return;
      }

      rootStyle.setProperty("--app-navbar-height", `${Math.round(nextHeight)}px`);
    };

    const scheduleNavbarHeightUpdate = () => {
      updateNavbarHeight();

      if (typeof window.requestAnimationFrame === "function") {
        if (frameId !== null) {
          window.cancelAnimationFrame(frameId);
        }

        frameId = window.requestAnimationFrame(() => {
          frameId = null;
          updateNavbarHeight();
        });
      }

      if (settleTimerId !== null) {
        window.clearTimeout(settleTimerId);
      }

      settleTimerId = window.setTimeout(() => {
        settleTimerId = null;
        updateNavbarHeight();
      }, 80);
    };

    scheduleNavbarHeightUpdate();

    let observer;
    if (typeof ResizeObserver !== "undefined" && navbarRef.current) {
      observer = new ResizeObserver(() => {
        scheduleNavbarHeightUpdate();
      });
      observer.observe(navbarRef.current);
    }

    window.addEventListener("resize", scheduleNavbarHeightUpdate);
    window.addEventListener("orientationchange", scheduleNavbarHeightUpdate);
    window.addEventListener(
      REFRESH_APP_CHROME_METRICS_EVENT,
      scheduleNavbarHeightUpdate
    );

    return () => {
      observer?.disconnect();
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      if (settleTimerId !== null) {
        window.clearTimeout(settleTimerId);
      }
      window.removeEventListener("resize", scheduleNavbarHeightUpdate);
      window.removeEventListener(
        "orientationchange",
        scheduleNavbarHeightUpdate
      );
      window.removeEventListener(
        REFRESH_APP_CHROME_METRICS_EVENT,
        scheduleNavbarHeightUpdate
      );
    };
  }, [activeTab, isMobileViewport, useReelitProChrome]);

  useEffect(() => {
    if (!showMenu) return;

    const handleClickOutside = (event) => {
      const clickedMenu = profileMenuRef.current?.contains(event.target);
      const clickedIcon = profileIconRef.current?.contains(event.target);

      if (!clickedMenu && !clickedIcon) {
        setShowMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMenu, setShowMenu]);

  useEffect(() => {
    if (!searchOpen) return;

    const handleClickOutside = (event) => {
      const clickedInside = searchWrapRef.current?.contains(event.target);
      if (!clickedInside) setSearchOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [searchOpen]);

  useEffect(() => {
    if (!hasAuthenticatedUser || authTransitioning) {
      setUnreadCount(0);
      return;
    }

    let cancelled = false;
    previousUnreadCountRef.current = null;

    const loadUnread = async () => {
      if (shouldBlockBackgroundRefresh()) {
        reportBackgroundRefreshBlocked({ source: "messages_unread" });
        return;
      }
      if (unreadRequestInFlightRef.current) {
        queuedUnreadRefreshRef.current = true;
        return activeUnreadPromiseRef.current || Promise.resolve();
      }

      unreadRequestInFlightRef.current = true;

      const unreadPromise = fetch(
        `${API_BASE}/messages/unread`,
        withApiCredentials({
          headers: token
            ? {
                Authorization: `Bearer ${token}`,
              }
            : {},
        })
      )
        .then(async (res) => {
          if (!res.ok) return;

          const data = await res.json();
          if (!cancelled) {
            const nextCount = Number(data.count) || 0;
            const previousCount = previousUnreadCountRef.current;

            setUnreadCount(nextCount);

            if (previousCount !== null && nextCount > previousCount) {
              playIncomingMessageSound();
              window.dispatchEvent(new Event("messagesUpdated"));
            }

            previousUnreadCountRef.current = nextCount;
          }
        })
        .catch((err) => {
          if (!cancelled) {
            console.error("Failed to load unread messages", err);
          }
        })
        .finally(() => {
          unreadRequestInFlightRef.current = false;
          activeUnreadPromiseRef.current = null;

          if (queuedUnreadRefreshRef.current && !cancelled) {
            queuedUnreadRefreshRef.current = false;
            loadUnread().catch(() => {});
          }
        });

      activeUnreadPromiseRef.current = unreadPromise;
      return unreadPromise;
    };

    loadUnread().catch(() => {});
    window.addEventListener("messagesRead", loadUnread);
    const interval = setInterval(loadUnread, 20000);

    return () => {
      cancelled = true;
      unreadRequestInFlightRef.current = false;
      queuedUnreadRefreshRef.current = false;
      activeUnreadPromiseRef.current = null;
      window.removeEventListener("messagesRead", loadUnread);
      clearInterval(interval);
    };
  }, [authTransitioning, hasAuthenticatedUser, token]);

  const trimmedSearchQuery = (searchQuery || "").trim();
  const q = trimmedSearchQuery.toLowerCase();
  const isMapSearch = activeTab === "map";
  const isGroupsSearch = activeTab === "groups";
  const shouldUseRpMobileTopChrome =
    useReelitProChrome && isMobileViewport;
  const shouldUseRpDesktopTopBar =
    useReelitProChrome && !isMobileViewport;
  const showMobileMapLocateButton =
    shouldUseRpMobileTopChrome &&
    isMapSearch &&
    typeof onMapLocateMe === "function";
  const shouldHideMobileCatchMarkerSearch = shouldHideMobileMapSearch({
    useMobileTopChrome: shouldUseRpMobileTopChrome,
    isMapSearch,
    isCatchMarkerDetailsOpen,
  });

  useEffect(() => {
    if (shouldHideMobileCatchMarkerSearch) {
      setSearchOpen(false);
    }

    window.dispatchEvent(new Event(REFRESH_APP_CHROME_METRICS_EVENT));
  }, [shouldHideMobileCatchMarkerSearch]);

  const filteredUsers =
    q.length === 0
      ? []
      : (searchResults || [])
          .filter((user) => (user?.username || "").toLowerCase().includes(q))
          .slice(0, 8);

  const filteredGroups =
    q.length === 0
      ? []
      : (groupSearchResults || [])
          .filter((group) => {
            const name = (group?.name || "").toLowerCase();
            const description = (group?.description || "").toLowerCase();
            return name.includes(q) || description.includes(q);
          })
          .slice(0, 8);

  useEffect(() => {
    if (!isMapSearch) {
      mapSearchAbortRef.current?.abort();
      mapSearchAbortRef.current = null;
      setMapSearchResults([]);
      setMapSearchLoading(false);
      setMapSearchError("");
      return undefined;
    }

    if (!searchOpen || trimmedSearchQuery.length < 3) {
      mapSearchAbortRef.current?.abort();
      mapSearchAbortRef.current = null;
      setMapSearchResults([]);
      setMapSearchLoading(false);
      setMapSearchError("");
      return undefined;
    }

    const controller = new AbortController();
    mapSearchAbortRef.current?.abort();
    mapSearchAbortRef.current = controller;
    setMapSearchLoading(true);
    setMapSearchError("");

    const timeoutId = window.setTimeout(() => {
      searchMapLocations(trimmedSearchQuery, {
        signal: controller.signal,
        limit: 6,
      })
        .then((locations) => {
          if (!controller.signal.aborted) {
            setMapSearchResults(locations);
          }
        })
        .catch((error) => {
          if (error?.name === "AbortError" || controller.signal.aborted) {
            return;
          }

          console.error("Map location search failed", error);
          setMapSearchResults([]);
          setMapSearchError(
            t("map_search_error", {
              defaultValue: "Could not search locations right now.",
            })
          );
        })
        .finally(() => {
          if (!controller.signal.aborted) {
            setMapSearchLoading(false);
          }
        });
    }, 400);

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
      if (mapSearchAbortRef.current === controller) {
        mapSearchAbortRef.current = null;
      }
    };
  }, [isMapSearch, searchOpen, t, trimmedSearchQuery]);

  const handlePickUser = (userResult) => {
    if (!userResult?.username) return;

    if (typeof onSelectSearchUser === "function") {
      onSelectSearchUser(userResult.username);
    }

    setSearchQuery("");
    setSearchOpen(false);

    if (setSidebarOpen) setSidebarOpen(false);
  };

  const handlePickGroup = (group) => {
    if (!group?.id) return;

    if (typeof onSelectSearchGroup === "function") {
      onSelectSearchGroup(group);
    }

    setSearchQuery("");
    setSearchOpen(false);

    if (setSidebarOpen) setSidebarOpen(false);
  };

  const handlePickMapLocation = (locationResult) => {
    const lat = Number(locationResult?.lat);
    const lng = Number(locationResult?.lng);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return;
    }

    const pendingLocation = {
      lat,
      lng,
      zoom: 6,
      openOverlay: false,
    };

    try {
      sessionStorage.setItem(
        "pendingMapLocation",
        JSON.stringify(pendingLocation)
      );
    } catch (error) {
      console.warn("Unable to persist pending map location", error);
    }

    navigate({
      pathname: "/",
      search: "?tab=map",
      hash: "",
    });

    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("openMapAtLocation", {
          detail: pendingLocation,
        })
      );
    }

    setSearchQuery("");
    setMapSearchResults([]);
    setSearchOpen(false);

    if (setSidebarOpen) setSidebarOpen(false);
  };

  const handleOpenMessagesInbox = () => {
    if (typeof onOpenMessagesInbox === "function") {
      onOpenMessagesInbox();
    } else if (typeof setShowInbox === "function") {
      setShowInbox(true);
    }

    if (setSidebarOpen) setSidebarOpen(false);
    navigate("/messages");
  };

  const handleMobileMapLocateClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setSearchOpen(false);
    onMapLocateMe?.();
  };

  const handleOpenDeleteAccountConfirm = () => {
    setShowMenu(false);
    onRequestDeleteAccount?.();
  };

  const handleOpenLogoutConfirm = () => {
    setShowMenu(false);
    if (typeof onRequestLogout === "function") {
      onRequestLogout();
      return;
    }
    onLogout?.();
  };

  const handleOpenSettings = () => {
    setShowMenu(false);
    setShowSettingsPanel(true);
  };

  const renderSearchDropdown = () => {
    if (!searchOpen || q.length === 0) {
      return null;
    }

    if (isMapSearch) {
      if (trimmedSearchQuery.length < 3) {
        return null;
      }

      return (
        <div className="nav-search-dropdown">
          {mapSearchLoading ? (
            <div className="nav-search-empty">
              {t("map_search_loading", { defaultValue: "Searching locations..." })}
            </div>
          ) : mapSearchError ? (
            <div className="nav-search-empty">{mapSearchError}</div>
          ) : mapSearchResults.length === 0 ? (
            <div className="nav-search-empty">
              {t("search_no_results", { defaultValue: "No results found" })}
            </div>
          ) : (
            mapSearchResults.map((locationResult) => (
              <button
                key={locationResult.id}
                type="button"
                className="nav-search-item"
                onClick={() => handlePickMapLocation(locationResult)}
              >
                <div className="nav-search-avatar nav-search-group-avatar">
                  {"\uD83D\uDCCD"}
                </div>
                <div className="nav-search-copy">
                  <span className="nav-search-name">{locationResult.label}</span>
                  <span className="nav-search-subtitle">
                    {locationResult.lat.toFixed(4)},{" "}
                    {locationResult.lng.toFixed(4)}
                  </span>
                </div>
              </button>
            ))
          )}
        </div>
      );
    }

    return (
      <div className="nav-search-dropdown">
        {isGroupsSearch ? (
          filteredGroups.length === 0 ? (
            <div className="nav-search-empty">{t("search_no_results")}</div>
          ) : (
            filteredGroups.map((group) => (
              <button
                key={group.id}
                type="button"
                className="nav-search-item"
                onClick={() => handlePickGroup(group)}
              >
                <div className="nav-search-avatar nav-search-group-avatar">
                  {(group?.name || "G").charAt(0).toUpperCase()}
                </div>
                <div className="nav-search-copy">
                  <span className="nav-search-name">{group.name}</span>
                  <span className="nav-search-subtitle">
                    {(group?.members || []).length}{" "}
                    {t("members", { defaultValue: "members" })}
                  </span>
                </div>
              </button>
            ))
          )
        ) : filteredUsers.length === 0 ? (
          <div className="nav-search-empty">{t("search_no_results")}</div>
        ) : (
          filteredUsers.map((userResult) => (
            <button
              key={userResult.id}
              type="button"
              className="nav-search-item"
              onClick={() => handlePickUser(userResult)}
            >
              <img
                className="nav-search-avatar"
                src={
                  userResult?.avatar
                    ? `${API_BASE}${userResult.avatar}`
                    : "https://cdn-icons-png.flaticon.com/512/847/847969.png"
                }
                alt={t("profile_picture")}
              />
              <span className="nav-search-name">{userResult.username}</span>
            </button>
          ))
        )}
      </div>
    );
  };

  const renderSearchInput = ({ wrapClassName, inputClassName }) => (
    <div className={wrapClassName} ref={searchWrapRef}>
      {isGroupsSearch ? (
        <Search
          className="groups-search-leading-icon"
          size={18}
          strokeWidth={2.25}
          aria-hidden="true"
        />
      ) : null}
      <input
        type="text"
        className={inputClassName}
        aria-label={
          isMapSearch
            ? t("map_search_placeholder", {
                defaultValue: "Search city, lake, sea...",
              })
            : isGroupsSearch
            ? t("groups_search_placeholder", { defaultValue: "Search groups" })
            : t("search_placeholder")
        }
        placeholder={
          isMapSearch
            ? t("map_search_placeholder", {
                defaultValue: "Search city, lake, sea...",
              })
            : isGroupsSearch
            ? t("groups_search_placeholder", { defaultValue: "Search groups" })
            : t("search_placeholder")
        }
        value={searchQuery}
        onFocus={() => setSearchOpen(true)}
        onChange={(event) => {
          setSearchQuery(event.target.value);
          setSearchOpen(true);
        }}
      />
      {showMobileMapLocateButton ? (
        <button
          type="button"
          className="rp-searchbar__locate-btn"
          aria-label={t("map_search_locate_me", {
            defaultValue: "My location",
          })}
          title={t("map_search_locate_me", {
            defaultValue: "My location",
          })}
          onClick={handleMobileMapLocateClick}
        >
          <LocateFixed size={18} strokeWidth={2.35} aria-hidden="true" />
        </button>
      ) : null}
      {renderSearchDropdown()}
    </div>
  );

  const renderMobileBrandTitle = () => {
    return (
      <>
        <span className="rp-navbar__wordmark-script">
          {t("navbar_mobile_brand_reel_it", { defaultValue: "Reel it" })}
        </span>
        <span className="rp-navbar__wordmark-pro">
          {t("navbar_mobile_brand_pro", { defaultValue: "PRO" })}
        </span>
      </>
    );
  };

  const renderDesktopBrandTitle = () => {
    const title = String(t("app_title") || "");
    const proMatch = title.match(/\bPRO\b/);

    if (!proMatch || typeof proMatch.index !== "number") {
      return title;
    }

    const proStart = proMatch.index;
    const proEnd = proStart + proMatch[0].length;

    return (
      <>
        <span className="rp-navbar__wordmark-script rp-deskbar__wordmark-script">
          {title.slice(0, proStart)}
        </span>
        <span className="rp-deskbar__wordmark-pro">
          {title.slice(proStart, proEnd)}
        </span>
        {title.slice(proEnd)}
      </>
    );
  };

  const mobileProfileAvatarStyle = {
    objectPosition: resolveAvatarObjectPosition(currentUser),
    transform: `scale(${resolveAvatarScale(currentUser)})`,
  };

  const profileDisplayName =
    currentUser?.displayName ||
    currentUser?.display_name ||
    currentUser?.name ||
    currentUser?.username ||
    t("app_title");
  const profileUsername = currentUser?.username
    ? `@${String(currentUser.username).replace(/^@/, "")}`
    : "";
  const profileProgression = currentUser?.progression || {};
  const profileRankName =
    profileProgression.currentRankName || profileProgression.rankName || "";
  const parsedProfileLevel = Math.floor(Number(profileProgression.currentLevel));
  const profileLevel = Number.isFinite(parsedProfileLevel)
    ? Math.max(1, parsedProfileLevel)
    : null;
  const profileProgressionLabel = [
    profileRankName,
    profileLevel
      ? t("progression_level_popup_pill", {
          defaultValue: "Level {{level}}",
          level: profileLevel,
        })
      : "",
  ]
    .filter(Boolean)
    .join(" - ");

  const renderProfileSection = ({ useCurrentAvatar = false } = {}) => (
    <div className="profile-section">
      {useCurrentAvatar ? (
        <button
          ref={profileIconRef}
          type="button"
          className="profile-icon profile-icon--button"
          aria-label={t("profile_picture")}
          aria-expanded={showMenu}
          aria-haspopup="true"
          onClick={() => setShowMenu((prev) => !prev)}
        >
          <img
            src={resolveAvatarSrc(currentUser)}
            alt=""
            className="profile-icon__image"
            style={mobileProfileAvatarStyle}
          />
        </button>
      ) : (
        <img
          ref={profileIconRef}
          src={DEFAULT_AVATAR}
          alt={t("profile_picture")}
          className="profile-icon"
          onClick={() => setShowMenu((prev) => !prev)}
        />
      )}

      {showMenu ? (
        <div
          ref={profileMenuRef}
          className="profile-menu"
          role="group"
          aria-label={t("profile_picture")}
        >
          <div className="profile-menu__header">
            <div className="profile-menu__avatar-wrap">
              <img
                src={resolveAvatarSrc(currentUser)}
                alt=""
                className="profile-menu__avatar"
                style={mobileProfileAvatarStyle}
              />
            </div>
            <div className="profile-menu__identity">
              <strong className="profile-menu__name">{profileDisplayName}</strong>
              {profileUsername && profileUsername !== `@${profileDisplayName}` ? (
                <span className="profile-menu__username">{profileUsername}</span>
              ) : null}
              {profileProgressionLabel ? (
                <span className="profile-menu__rank">
                  <Trophy size={12} strokeWidth={2.4} aria-hidden="true" />
                  {profileProgressionLabel}
                </span>
              ) : null}
            </div>
          </div>

          <div className="profile-menu__actions">
            <button
              type="button"
              className="profile-menu__action"
              onClick={() => setShowProfile(true)}
            >
              <span className="profile-menu__action-icon" aria-hidden="true">
                <UserRound size={18} strokeWidth={2.25} />
              </span>
              <span className="profile-menu__action-label">{t("view_profile")}</span>
              <ChevronRight
                className="profile-menu__chevron"
                size={17}
                strokeWidth={2.25}
                aria-hidden="true"
              />
            </button>
            <button
              type="button"
              className="profile-menu__action"
              onClick={handleOpenSettings}
            >
              <span className="profile-menu__action-icon" aria-hidden="true">
                <Settings size={18} strokeWidth={2.25} />
              </span>
              <span className="profile-menu__action-label">{t("settings")}</span>
              <ChevronRight
                className="profile-menu__chevron"
                size={17}
                strokeWidth={2.25}
                aria-hidden="true"
              />
            </button>
            <button
              type="button"
              className="profile-menu__action profile-menu-danger"
              onClick={handleOpenDeleteAccountConfirm}
            >
              <span className="profile-menu__action-icon" aria-hidden="true">
                <Trash2 size={18} strokeWidth={2.25} />
              </span>
              <span className="profile-menu__action-label">
                {t("delete_account", { defaultValue: "Delete Account" })}
              </span>
            </button>
            <button
              type="button"
              className="profile-menu__action"
              onClick={handleOpenLogoutConfirm}
            >
              <span className="profile-menu__action-icon" aria-hidden="true">
                <LogOut size={18} strokeWidth={2.25} />
              </span>
              <span className="profile-menu__action-label">{t("logout")}</span>
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );

  return (
    <>
      <div ref={navbarRef}>
        {shouldUseRpMobileTopChrome ? (
          <div className="rp-mobile-topchrome">
            <div className="rp-navbar">
              <button
                type="button"
                className="rp-navbar__menu-btn mobile-menu-btn"
                aria-label={t("common_menu", { defaultValue: "Open menu" })}
                onClick={() => setSidebarOpen((prev) => !prev)}
              >
                <Menu size={23} strokeWidth={2.25} aria-hidden="true" />
              </button>

              <div className="rp-navbar__logo">
                <img
                  src="/banner/logo.png"
                  alt={t("app_title")}
                  className="rp-navbar__logo-img"
                />
                <div className="rp-navbar__wordmark">{renderMobileBrandTitle()}</div>
              </div>

              <div className="rp-navbar__actions">
                <div className="rp-navbar__lang-btn">
                  <LanguageSwitcher />
                </div>

                <button
                  className="nav-messages-btn rp-navbar__icon-btn"
                  title={t("messages")}
                  aria-label={t("messages")}
                  onClick={handleOpenMessagesInbox}
                >
                  <MessageCircleMore size={23} strokeWidth={2.25} aria-hidden="true" />
                  {unreadCount > 0 ? (
                    <span className="notif-count rp-navbar__badge">
                      {unreadCount}
                    </span>
                  ) : null}
                </button>

                <div className="rp-navbar__icon-btn">
                  <FriendNotifications
                    currentUser={currentUser}
                    users={searchResults}
                    onOpenNotificationPost={onOpenNotificationPost}
                    bellIcon={<Bell size={23} strokeWidth={2.25} />}
                  />
                </div>

                <div className="rp-navbar__icon-btn rp-navbar__icon-btn--profile">
                  {renderProfileSection({ useCurrentAvatar: true })}
                </div>
              </div>
            </div>

            {!shouldHideMobileCatchMarkerSearch ? (
              <div className="rp-searchbar">
                <div className="rp-searchbar__inner">
                  {renderSearchInput({
                    wrapClassName: "nav-search-wrap rp-searchbar__wrap",
                    inputClassName: "rp-searchbar__input",
                  })}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {shouldUseRpDesktopTopBar ? (
          <div className="rp-deskbar">
            <div className="rp-deskbar__logo">
              <img
                src="/banner/logo.png"
                alt={t("app_title")}
                className="nav-logo rp-deskbar__logo-img"
              />
              <h1 className="logo-text rp-deskbar__wordmark">
                {renderDesktopBrandTitle()}
              </h1>
            </div>

            <div className="rp-deskbar__search">
              {renderSearchInput({
                wrapClassName: "nav-search-wrap rp-deskbar__search-wrap",
                inputClassName: "search-bar rp-deskbar__search-input",
              })}
            </div>

            <div className="nav-right rp-deskbar__actions">
              <div className="rp-deskbar__lang">
                <LanguageSwitcher />
              </div>

              <button
                className="nav-messages-btn rp-deskbar__icon-btn"
                title={t("messages")}
                aria-label={t("messages")}
                onClick={handleOpenMessagesInbox}
              >
                <MessageCircleMore size={23} strokeWidth={2.25} aria-hidden="true" />
                {unreadCount > 0 ? (
                  <span className="notif-count">{unreadCount}</span>
                ) : null}
              </button>

              <div className="rp-deskbar__icon-btn">
                <FriendNotifications
                  currentUser={currentUser}
                  users={searchResults}
                  onOpenNotificationPost={onOpenNotificationPost}
                  bellIcon={<Bell size={23} strokeWidth={2.25} />}
                />
              </div>

              <div className="rp-deskbar__profile">
                {renderProfileSection({ useCurrentAvatar: true })}
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <SettingsPanel
        isOpen={showSettingsPanel}
        onClose={() => setShowSettingsPanel(false)}
        appMode={appMode}
        onAppModeChange={onAppModeChange}
        themeMode={themeMode}
        onThemeModeChange={onThemeModeChange}
        currentUser={currentUser}
      />
    </>
  );
}
